// FinancialProjections.jsx
import { useEffect, useRef, useState } from "react";
import { CardText } from "react-bootstrap";
import {
  RadialBarChart, RadialBar, ResponsiveContainer,
  Cell, Tooltip
} from "recharts";

// ── Raw inputs ──────────────────────────────────────────────
const COMPS = [
  { film: "John Wick",     budget: 20,  domestic: 88,  international: 53  },
  { film: "The Equalizer", budget: 55,  domestic: 192, international: 108 },
  { film: "Limitless",     budget: 27,  domestic: 161, international: 78  },
  { film: "Upgrade",       budget: 3,   domestic: 16,  international: 9   },
  { film: "Falling Down",  budget: 25,  domestic: 41,  international: 22  },
];

const PRODUCTION_BUDGET = 30_000_000;
const PA_RATIO          = 0.50;
const OTHER_COST_RATIO  = 0.10;
const DOM_DIST_SHARE    = 0.50;
const INTL_DIST_SHARE   = 0.35;

const TAX = {
  baseGrant:      7_500_000,
  ruralGrant:     750_000,
  postBonus:      300_000,
  get total()    { return this.baseGrant + this.ruralGrant + this.postBonus; },
};

// ── Derived projections ──────────────────────────────────────
const avgDomestic      = COMPS.reduce((s, c) => s + c.domestic, 0)      / COMPS.length;
const avgInternational = COMPS.reduce((s, c) => s + c.international, 0) / COMPS.length;

const domesticGross        = avgDomestic      * 1_000_000;
const internationalGross   = avgInternational * 1_000_000;
const domesticNet          = domesticGross      * (1 - DOM_DIST_SHARE);
const internationalNet     = internationalGross * (1 - INTL_DIST_SHARE);
const totalRevenue         = domesticNet + internationalNet;

const pAndA                = PRODUCTION_BUDGET * PA_RATIO;
const otherCosts           = PRODUCTION_BUDGET * OTHER_COST_RATIO;
const totalCosts           = PRODUCTION_BUDGET + pAndA + otherCosts;

const netProfitOriginal    = totalRevenue - totalCosts;
const netProfitAdjusted    = netProfitOriginal + TAX.total;
const effectiveCosts       = totalCosts - TAX.total;
const roiOriginal          = (netProfitOriginal / totalCosts) * 100;
const roiAdjusted          = (netProfitAdjusted / effectiveCosts) * 100;

// ── Helpers ──────────────────────────────────────────────────
const fmt = (n) => {
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1_000_000) {
    return `${sign}$${parseFloat((abs / 1_000_000).toFixed(2))}M`;
  } else if (abs >= 1_000) {
    return `${sign}$${(abs / 1_000).toFixed(0)}K`;
  }
  return `${sign}$${abs.toLocaleString()}`;
};


const fmtPct = (n) => `${n.toFixed(1)}%`;

const SECTIONS = [
  {
    title: "Costs",
    color: "rgba(233, 134, 134, 0.8)",
    rows: [
      { label: "Production Budget",  value: fmt(PRODUCTION_BUDGET) },
      { label: "P&A (50%)",          value: fmt(pAndA)             },
      { label: "Other Costs (10%)",  value: fmt(otherCosts)        },
      { label: "Total Costs",        value: fmt(totalCosts), bold: true },
    ],
  },
  {
    title: "Revenue Projections",
    color: "rgba(100,200,100,0.8)",
    rows: [
      { label: "Domestic Gross",              value: fmt(domesticGross)      },
      { label: "Domestic Net (after 50%)",    value: fmt(domesticNet)        },
      { label: "International Gross",         value: fmt(internationalGross) },
      { label: "International Net (after 35%)", value: fmt(internationalNet) },
      { label: "Total Revenue",               value: fmt(totalRevenue), bold: true },
    ],
  },
  {
    title: "Tax Incentives (TMIIIF)",
    color: "rgba(243,180,61,0.8)",
    rows: [
      { label: "Base Grant (25%)",          value: fmt(TAX.baseGrant)  },
      { label: "Rural Filming Grant (2.5%)",value: fmt(TAX.ruralGrant) },
      { label: "Post-Production Bonus (1%)",value: fmt(TAX.postBonus)  },
      { label: "Total Incentive",           value: fmt(TAX.total), bold: true },
    ],
  },
  {
    title: "Final Projections",
    color: "rgba(100,160,243,0.8)",
    rows: [
      { label: "Net Profit (Original)",          value: fmt(netProfitOriginal)  },
      { label: "Net Profit (with Incentives)",   value: fmt(netProfitAdjusted), bold: true },
      { label: "Effective Total Costs",          value: fmt(effectiveCosts)     },
      { label: "ROI (Original)",                 value: fmtPct(roiOriginal)     },
      { label: "ROI (with Incentives)",          value: fmtPct(roiAdjusted), bold: true },
    ],
  },
];

const ROI_DATA = [
  { name: "ROI w/ Incentives", value: Math.min(roiAdjusted, 200), fill: "rgba(100,160,243,0.85)" },
  { name: "ROI Original",      value: Math.min(roiOriginal, 200),  fill: "rgba(243,61,61,0.75)"  },
];

// ── Component ────────────────────────────────────────────────
export default function FinancialProjections() {
  const [activeSection, setActiveSection] = useState(null);
  const [visible, setVisible] = useState(false);
  const wrapperRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (wrapperRef.current) observer.observe(wrapperRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={wrapperRef}
      style={{
        width: "100%",
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(30px)",
        transition: "opacity 0.7s ease, transform 0.7s ease",
        color: "white",
      }}
    >
      {/* <h10 style={{ textAlign: "center", marginBottom: "1rem" }}>
        Financial Projections
      </h10>
            <CardText style={{ padding: '0.6rem', textAlign: 'left' }}>
        Budget is $30 million with much of the costs offset through partnerships with local brands.
        Combining this with our ability to produce films on lean budgets, we're confident in our ability
        to maximize every dollar and deliver a visually stunning, commercially successful film. We're open to various structures, including equity investment and co-production deals.
        Our goal is to align with partners who share our vision and can help us bring this
        exciting project to life.
      </CardText> */}

      <div style={{ display: "flex", gap: "3rem", alignItems: "flex-start", flexWrap: "wrap", width: "100%", paddingTop: "3rem" }}>

        {/* ── LEFT: summary table ── */}
        <div style={{ flex: 2, display: "flex", flexDirection: "column", gap: "1.2rem" }}>
          {SECTIONS.map((section, si) => (
            <div
              key={section.title}
              onMouseEnter={() => setActiveSection(si)}
              onMouseLeave={() => setActiveSection(null)}
              style={{
                borderLeft: `3px solid ${section.color}`,
                paddingLeft: "0.8rem",
                opacity: activeSection === null || activeSection === si ? 1 : 0.4,
                transition: "opacity 0.2s",
              }}
            >
              <div style={{
                fontSize: 13,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: section.color,
                marginBottom: "0.4rem",
              }}>
              <span style={{ 
                color: section.color, 
                fontFamily: "'Buzzer', sans-serif",
                fontSize: 'clamp(2rem, 5vw, 2rem)'
              }}>
                {section.title}
              </span>
                
              </div>
              {section.rows.map((row) => (
                <div
                  key={row.label}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: row.bold ? 15 : 13,
                    fontWeight: row.bold ? "bold" : "normal",
                    opacity: row.bold ? 1 : 0.75,
                    padding: "0.15rem 0",
                    borderTop: row.bold ? "1px solid rgba(255,255,255,0.1)" : "none",
                    marginTop: row.bold ? "0.3rem" : 0,
                  }}
                >
                  <span
                  style={{ 
                  //  color: section.color, 
                   fontSize: '1rem'
                  }}>
                    {row.label}
                  </span>
                  <span 
                  style={{ 
                   color: section.color, 
                   fontSize: '1rem'
                  }}
                  >
                    {row.value}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* ── RIGHT: ROI radial visual ── */}
        <div style={{
          flex: 1,
          
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "1rem",
          paddingTop: "1rem",
        }}>
          {/* <div style={{ fontSize: 13, letterSpacing: "0.08em", textTransform: "uppercase", opacity: 0.6 }}>
            ROI Comparison
          </div> */}
          <h11>
            ROI Comparison
          </h11>

          <div style={{ width: "100%", height: 260, position: "relative" }}>
            <ResponsiveContainer>
              <RadialBarChart
                cx="50%" cy="50%"
                innerRadius="40%" outerRadius="100%"
                startAngle={180} endAngle={-180}
                data={ROI_DATA}
                barSize={18}
              >
                <RadialBar dataKey="value" background={{ fill: "rgba(255,255,255,0.05)" }} cornerRadius={6}>
                  {ROI_DATA.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} />
                  ))}
                </RadialBar>
                <Tooltip
                  formatter={(v) => [`${v.toFixed(1)}%`, "ROI"]}
                  contentStyle={{ background: "#111", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8 }}
                  labelStyle={{ color: "white" }}
                  itemStyle={{ color: "white" }}
                />
              </RadialBarChart>
            </ResponsiveContainer>

            {/* center label */}
            <div style={{
              position: "absolute", top: "50%", left: "50%",
              transform: "translate(-50%, -50%)",
              textAlign: "center", pointerEvents: "none",
            }}>
              <div style={{ fontSize: '1.5rem', fontWeight: "bold", color: "rgba(100,160,243,0.95)" }}>
                {fmtPct(roiAdjusted)}
              </div>
              <div style={{ fontSize: '1rem', opacity: 0.6 }}>w/ incentives</div>
            </div>
          </div>

          {/* legend */}
          {ROI_DATA.map((d) => (
            <div key={d.name} style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: 13 }}>
              <span style={{ width: 15, height: 15, borderRadius: "50%", background: d.fill, display: "inline-block" }} />
              <span style={{fontSize: '1rem', opacity: 0.8 }}>{d.name}: <strong>{fmtPct(d.value)}</strong></span>
            </div>
          ))}

          {/* key stats */}
          <div style={{
            marginTop: "1rem", width: "100%",
            display: "flex", flexDirection: "column", gap: "0.5rem", 
            borderTop: "1px solid rgba(255,255,255,0.1)", paddingTop: "1rem",
          }}>
            {[
              { label: "Net Profit (original)",    value: fmt(netProfitOriginal),  color: "rgba(243,61,61,0.9)"   },
              { label: "Net Profit (incentives)",  value: fmt(netProfitAdjusted),  color: "rgba(100,160,243,0.9)" },
              { label: "Incentives Received",      value: fmt(TAX.total),          color: "rgba(243,180,61,0.9)"  },
            ].map((stat) => (
              <div key={stat.label} style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                <span style={{ opacity: 0.7, fontSize: '1rem' }}>{stat.label}</span>
                <span style={{ color: stat.color, fontWeight: "bold", fontSize: '1rem' }}>{stat.value}</span>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}